import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryJobs } from '@/features/jobs/data/jobs';
import { serialiseSelectedIds } from '@/features/jobs/hooks/useJobsPageViewModel.helpers';
import { queryPipelines } from '@/features/pipelines/data';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { queryKeys } from '@/shared/hooks';
import { buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';

type JobsPageOptionQueriesInput = {
  effectiveProjectId: string | null;
  enabled: boolean;
  selectedOrganisationId: string | null;
  selectedPipelineIds: string[];
  selectedStageNames: string[];
};

type JobsPageOption = {
  label: string;
  value: string;
};

type PipelineOption = JobsPageOption & {
  projectId: string;
};

type JobsPageOptionQueriesResult = {
  jobOptions: JobsPageOption[];
  pipelineOptions: PipelineOption[];
  stageOptions: JobsPageOption[];
};

export const useJobsPageOptionQueries = ({
  effectiveProjectId,
  enabled,
  selectedOrganisationId,
  selectedPipelineIds,
  selectedStageNames,
}: JobsPageOptionQueriesInput): JobsPageOptionQueriesResult => {
  const organisationId = selectedOrganisationId ?? undefined;
  const pipelineOptionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/pipeline-options',
      selectedOrganisationId,
      effectiveProjectId,
    ),
    enabled,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelines(effectiveProjectId, organisationId, {
            page,
            pageSize,
            sorts: [{ field: 'name', direction: 'asc' }],
            filters: [],
          }),
      ),
  });
  const pipelineOptions = useMemo(
    () =>
      (pipelineOptionsQuery.data?.items ?? [])
        .map((pipeline) => ({
          value: pipeline.id,
          label: pipeline.name,
          projectId: pipeline.projectId,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [pipelineOptionsQuery.data?.items],
  );

  const stageOptionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/stage-options',
      selectedOrganisationId,
      effectiveProjectId,
      serialiseSelectedIds(selectedPipelineIds),
    ),
    enabled,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, organisationId, {
            page,
            pageSize,
            sorts: [{ field: 'stageName', direction: 'asc' }],
            filters: buildInFilter('pipelineId', selectedPipelineIds),
          }),
      ),
  });
  const stageOptions = useMemo(
    () =>
      Array.from(
        new Set((stageOptionsQuery.data?.items ?? []).map((job) => job.stageName?.trim() ?? '')),
      )
        .filter((stageName) => stageName.length > 0)
        .map((stageName) => ({ value: stageName, label: stageName }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [stageOptionsQuery.data?.items],
  );

  const jobOptionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'jobs/job-options',
      selectedOrganisationId,
      effectiveProjectId,
      serialiseSelectedIds(selectedPipelineIds),
      serialiseSelectedIds(selectedStageNames),
    ),
    enabled,
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryJobs(effectiveProjectId, organisationId, {
            page,
            pageSize,
            sorts: [{ field: 'jobName', direction: 'asc' }],
            filters: mergeFilters(
              buildInFilter('pipelineId', selectedPipelineIds),
              buildInFilter('stageName', selectedStageNames),
            ),
          }),
      ),
  });
  const jobOptions = useMemo(
    () =>
      Array.from(new Set((jobOptionsQuery.data?.items ?? []).map((job) => job.jobName.trim())))
        .filter((jobName) => jobName.length > 0)
        .map((jobName) => ({ value: jobName, label: jobName }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [jobOptionsQuery.data?.items],
  );

  return { jobOptions, pipelineOptions, stageOptions };
};
